import type { SVGProps } from 'react';

/**
 * The seventeen navigation glyphs, plus the four top-bar glyphs the shell needs
 * beside the ones already in `./index.tsx`.
 *
 * Every path is transcribed **verbatim** from the UI/UX pack's own markup: the
 * pack's §3.4.1 fixed each shape against a collision it had already met once —
 * a cog whose teeth are a closed outline so it cannot converge with the theme
 * toggle's sun, a clapperboard rather than a document because this console
 * manages films, a life ring rather than a briefcase because the case queue is
 * support work, a single figure with a check for accounts against a group for
 * credited people. Redrawing one here would re-open a collision the pack closed.
 *
 * Direction is applied where the design system says it applies and nowhere else.
 * The category tree grows its trunk at the reading-start edge and the
 * informational-page corner is turned at the reading end — both are already
 * drawn that way in the source paths, so no glyph carries a mirroring class: a
 * transform on top of a path authored for `rtl` would flip it back.
 */

type IconProps = SVGProps<SVGSVGElement>;

/** The column's glyphs sit at 18px, the size the pack draws them at. */
function NavIcon({ children, ...props }: IconProps) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/** لوحة التشغيل — a gauge. A tile grid read as «layout» and collided with the shelves. */
export function GaugeIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M3.5 18a8.5 8.5 0 1117 0" />
      <path d="M3.5 18h17" />
      <path d="M12 18l3.7-4.7" />
    </NavIcon>
  );
}

/** المحتوى — a clapperboard: what this console manages is films and series. */
export function ClapperboardIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M3 10h18v9a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <path d="M3.2 10l1-4.1a1 1 0 011.2-.7l13.1 3.2a1 1 0 01.7 1.2L18.9 10" />
      <path d="M8.6 6.2L7.4 10M13.7 7.4L12.5 10" />
    </NavIcon>
  );
}

/** الرفوف — two shelves with titles standing on them. */
export function ShelvesIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M3 10.5h18M3 19h18" />
      <path d="M6.5 10.5V7h3.2v3.5M13.2 10.5V5h3.3v5.5" />
      <path d="M6.5 19v-3.2h4V19M14 19v-2.4h3.2V19" />
    </NavIcon>
  );
}

/** التصنيفات — a tree whose trunk sits at the reading-start edge. */
export function TreeIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M18 4v13a2 2 0 01-2 2h-3" />
      <path d="M18 9.5h-5M18 14.5h-5" />
      <circle cx="18" cy="4" r="1.6" />
    </NavIcon>
  );
}

/** الوسوم — a label with its eyelet. */
export function TagIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M4 12.5V5a1 1 0 011-1h7.5L20 11.5 12.5 19z" />
      <circle cx="8.5" cy="8.5" r="1.2" />
    </NavIcon>
  );
}

/** المنسوبون — a group: cast and crew are many and hold no account. */
export function PeopleIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <circle cx="9" cy="8.2" r="3.2" />
      <path d="M2.8 19.6a6.2 6.2 0 0112.4 0" />
      <path d="M15.4 5.6a3.2 3.2 0 010 5.2M17.6 19.6a6.4 6.4 0 00-1.7-4.4" />
    </NavIcon>
  );
}

/** المقالات — a folded newspaper: an editorial stream. */
export function NewspaperIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M17.5 20H5a1.5 1.5 0 01-1.5-1.5V4.5h14z" />
      <path d="M17.5 8h3v10.5a1.5 1.5 0 01-3 0z" />
      <path d="M6.8 8h7.4M6.8 11.6h7.4M6.8 15.2h4.6" />
    </NavIcon>
  );
}

/** المستخدمون — one figure with a check: this surface grants roles and blocks accounts. */
export function UserCheckIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <circle cx="10" cy="7.8" r="3.8" />
      <path d="M3 20a7 7 0 0111-5.7" />
      <path d="M14.8 17.6l2.3 2.3 4.1-4.6" />
    </NavIcon>
  );
}

/** التعليقات — a comment carrying a decision. */
export function CommentCheckIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M4 5h16v11H8l-4 3z" />
      <path d="M8.5 10.5l2 2 4-4" />
    </NavIcon>
  );
}

/** الحالات — a life ring: the queue is support work, not a portfolio. */
export function LifeRingIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="M5.6 5.6l3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" />
    </NavIcon>
  );
}

/** الخطط — stacked tiers: plans are subscription levels. */
export function LayersIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M12 2.8l8.6 4.3L12 11.4 3.4 7.1z" />
      <path d="M3.4 12.1L12 16.4l8.6-4.3" />
      <path d="M3.4 16.9L12 21.2l8.6-4.3" />
    </NavIcon>
  );
}

/** تقارير الاشتراك — columns on an axis. Not the in-table share bar, which is a progress bar. */
export function BarsIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M3.5 20.5h17" />
      <path d="M6.8 20.5v-5.2M12 20.5V8.4M17.2 20.5v-8.3" />
    </NavIcon>
  );
}

/** بوّابات الدفع — a bank card: settlement is direct through the active gateway. */
export function CardIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10h19" />
      <path d="M6.5 14.6h3.4" />
    </NavIcon>
  );
}

/** الصفحات التعريفية — one document with its corner turned at the reading end. */
export function DocumentIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M10 3h7a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V8z" />
      <path d="M10 3v5H5" />
      <path d="M8.6 13h7M8.6 16.8h4.6" />
    </NavIcon>
  );
}

/** الإعدادات — a cog whose teeth are a closed outline, so it never converges with the sun. */
export function CogIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M10.4 3.3a1.6 1.6 0 013.2 0l.15 1.05a7.4 7.4 0 011.86.77l.85-.64a1.6 1.6 0 012.26 2.26l-.64.85c.33.58.59 1.2.77 1.86l1.05.15a1.6 1.6 0 010 3.2l-1.05.15c-.18.66-.44 1.28-.77 1.86l.64.85a1.6 1.6 0 01-2.26 2.26l-.85-.64c-.58.33-1.2.59-1.86.77l-.15 1.05a1.6 1.6 0 01-3.2 0l-.15-1.05a7.4 7.4 0 01-1.86-.77l-.85.64a1.6 1.6 0 01-2.26-2.26l.64-.85a7.4 7.4 0 01-.77-1.86l-1.05-.15a1.6 1.6 0 010-3.2l1.05-.15c.18-.66.44-1.28.77-1.86l-.64-.85a1.6 1.6 0 012.26-2.26l.85.64c.58-.33 1.2-.59 1.86-.77z" />
      <circle cx="12" cy="12" r="3" />
    </NavIcon>
  );
}

// ---- top-bar glyphs -------------------------------------------------------

/** The notification control. It lives in the bar, where it is the only bell. */
export function BellIcon(props: IconProps) {
  return (
    <NavIcon {...props} width={props.width ?? '1.125em'} height={props.height ?? '1.125em'}>
      <path d="M18 8.8a6 6 0 10-12 0c0 5.1-2 6.8-2 6.8h16s-2-1.7-2-6.8" />
      <path d="M13.7 19.1a2 2 0 01-3.4 0" />
    </NavIcon>
  );
}

/** The primary create action. */
export function PlusIcon(props: IconProps) {
  return (
    <NavIcon {...props} width={props.width ?? '1.125em'} height={props.height ?? '1.125em'}>
      <path d="M12 5v14M5 12h14" />
    </NavIcon>
  );
}

/** The top bar's search handle. */
export function SearchIcon(props: IconProps) {
  return (
    <NavIcon {...props} width={props.width ?? '1.125em'} height={props.height ?? '1.125em'}>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5L21 21" />
    </NavIcon>
  );
}

/** Sign-out, in the account menu. */
export function SignOutIcon(props: IconProps) {
  return (
    <NavIcon {...props}>
      <path d="M14 4h4a1 1 0 011 1v14a1 1 0 01-1 1h-4" />
      <path d="M9 8l-4 4 4 4M5 12h9" />
    </NavIcon>
  );
}
